import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Icons } from '../Icons';
import Label from 'components/product/components/primitives/Label';

/**
 * Tag Filter Component
 * Multiselect, searchable tag filter for provider products
 */
export const TagFilter = ({
  tags = [],
  selectedTags = [],
  onTagsChange,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter tags based on search query
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return tags;
    }
    const query = searchQuery.toLowerCase();
    return tags.filter(tag => tag.toLowerCase().includes(query));
  }, [tags, searchQuery]);

  const handleTagToggle = (tag) => {
    const newSelectedTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    onTagsChange(newSelectedTags);
  };

  const handleRemoveTag = (tag, e) => {
    e.stopPropagation();
    const newSelectedTags = selectedTags.filter(t => t !== tag);
    onTagsChange(newSelectedTags);
  };

  return (
    <div className="filter-group" ref={dropdownRef}>
      <Label className="filter-label">Tags</Label>
      <div style={{ position: 'relative' }}>
        {/* Selected Tags Display */}
        <div
          className="tag-filter-input"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            minHeight: '40px',
            padding: '8px 12px',
            border: '1px solid var(--border-light)',
            borderRadius: '6px',
            background: 'var(--bg-primary)',
            cursor: 'pointer',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          {selectedTags.length > 0 ? (
            selectedTags.map(tag => (
              <span
                key={tag}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  background: 'var(--primary)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}
              >
                {tag}
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(tag, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Select tags...
            </span>
          )}
          <Icons.ChevronDown 
            size={16} 
            style={{ 
              marginLeft: 'auto',
              color: 'var(--text-secondary)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 10000,
              maxHeight: '300px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Search Input */}
            <div
              style={{
                padding: '8px',
                borderBottom: '1px solid var(--border-light)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '4px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <Icons.Search size={14} style={{ color: 'var(--text-secondary)' }} />
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Tag List */}
            <div
              style={{
                maxHeight: '240px',
                overflowY: 'auto',
                padding: '4px'
              }}
            >
              {loading ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Loading tags...
                </div>
              ) : filteredTags.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  {searchQuery ? 'No tags found' : 'No tags available'}
                </div>
              ) : (
                filteredTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagToggle(tag)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      textAlign: 'left',
                      background: selectedTags.includes(tag) 
                        ? 'var(--primary-light)' 
                        : 'transparent',
                      border: 'none',
                      color: selectedTags.includes(tag) 
                        ? 'var(--primary)' 
                        : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedTags.includes(tag)) {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedTags.includes(tag)) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        border: `2px solid ${selectedTags.includes(tag) ? 'var(--primary)' : 'var(--border-light)'}`,
                        borderRadius: '3px',
                        background: selectedTags.includes(tag) ? 'var(--primary)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {selectedTags.includes(tag) && (
                        <span style={{ color: 'white', fontSize: '12px', lineHeight: '1' }}>✓</span>
                      )}
                    </span>
                    <span>{tag}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

